import { FunctionComponent, useEffect, useState } from 'react'
import styles from './childPlanningHandler.module.scss'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Add, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/modules/context/AuthContext'
import { Daycare, DaycareType } from '@/types/Daycare'
import { useLazyQuery } from '@apollo/client'
import { GET_LAST_REGULAR_SLOT, GET_REGULAR_SLOTS } from '@/modules/API/slot/regularSlotHandler'
import { OccasionalSlotsPlaces, RegularSlot } from '@/types/Slots'
import moment, { Moment } from 'moment-timezone'
import ChildPlanning from './childPlanning/childPlanning'
import { getClosingDaycareDays, getDateClosingDays } from '../../../../planning/utils/calendarParameters'
import { DateSelectArg, EventClickArg } from '@fullcalendar/core'
import { EventCalendar } from '@/types/Calendar'
import ShowSlot from '../../../../planning/Dialog/showEvent'
import { useScreenSize } from '@/modules/desktopMobile/screenSizeContext'
import { dataRegularSlots } from '../../../../planning/utils/eventsRegularSlots'
import { GET_CLOSING_DAY } from '@/modules/API/daycare/closingDayHandler'
import { ClosingDay } from '@/types/ClosingDays'
import { dataClosingDays } from '../../../../planning/utils/eventsClosingDays'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import { GET_PLACES } from '@/modules/API/daycare/daycareHandler.ts'
import AddRegularSlotsDialog from '@/container/planning/Dialog/regularSlot/addRegularSlot/addRegularSlotsDialog.tsx'

interface ChildPlanningHandlerProps {
    daycareId: number
    childId: number
}

const NUMBER_OF_DAYS: number = 7

const ChildPlanningHandler: FunctionComponent<ChildPlanningHandlerProps> = ({
    daycareId,
    childId
}: ChildPlanningHandlerProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const [showRegularInformation, setShowRegularInformation] = useState<boolean>(true)

    const [addRegularSlotDialog, setAddRegularSlotDialog] = useState<boolean>(false)

    const [daycareInformation, setDaycareInformation] = useState<Daycare | undefined>(
        user?.daycares.find((daycare: Daycare): boolean => daycare.id === daycareId) || undefined
    )

    const [places, setPlaces] = useState<OccasionalSlotsPlaces[]>([])

    const [getDaycarePlaces, { data: dataDaycarePlaces }] = useLazyQuery(GET_PLACES)

    useEffect(() => {
        if (dataDaycarePlaces) {
            setPlaces(dataDaycarePlaces.getOccasionalSlotsPlaces)
        }
    }, [dataDaycarePlaces])

    const [fromDate, setFromDate] = useState<Moment>(moment().startOf('month'))
    const [toDate, setToDate] = useState<Moment>(moment().endOf('month'))

    const [regularSlotDay, setRegularSlotDay] = useState<number[]>([])
    const [regularSlotStartDate, setRegularSlotStartDate] = useState<string | undefined>(undefined)
    const [regularSlotEndDate, setRegularSlotEndDate] = useState<string | undefined>(undefined)

    const [hiddenDays, setHiddenDays] = useState<number[]>(
        daycareInformation ? getClosingDaycareDays(daycareInformation.opening) : []
    )

    const [lastRegularSlot, setLastRegularSlot] = useState<RegularSlot | undefined>(undefined)

    const [getLastRegularSlot, { data: dataGetLastRegularSlot }] = useLazyQuery(GET_LAST_REGULAR_SLOT)

    useEffect(() => {
        if (dataGetLastRegularSlot && dataGetLastRegularSlot.getLastRegularSlot) {
            setLastRegularSlot(dataGetLastRegularSlot.getLastRegularSlot)
        } else {
            setLastRegularSlot(undefined)
        }
    }, [dataGetLastRegularSlot])

    const [regularSlots, setRegularSlots] = useState<RegularSlot[]>([])

    const [getRegularSlots, { data: dataGetRegularSlots }] = useLazyQuery(GET_REGULAR_SLOTS)

    useEffect(() => {
        if (dataGetRegularSlots) {
            setRegularSlots(
                dataGetRegularSlots.getRegularSlots
                    .slice()
                    .sort((eventA: RegularSlot, eventB: RegularSlot): number => {
                        return moment(eventA.start, 'HH:mm:ss').format('HH:mm') >
                            moment(eventB.start, 'HH:mm:ss').format('HH:mm')
                            ? -1
                            : 1
                    })
                    .sort((eventA: RegularSlot, eventB: RegularSlot): number => {
                        return eventA.day > eventB.day ? 1 : -1
                    })
            )
        }
    }, [dataGetRegularSlots])

    async function refetchAllRegularSlots(from: Moment, to: Moment): Promise<void> {
        setFromDate(from)
        setToDate(to)

        await getRegularSlots({
            variables: {
                daycareId: daycareId,
                childIds: [childId],
                from: from.format('DD-MM-YYYY'),
                to: to.format('DD-MM-YYYY')
            }
        })
        await getClosingDays({
            variables: {
                daycare_id: daycareId,
                from: from.format('DD-MM-YYYY'),
                to: to.format('DD-MM-YYYY')
            }
        })
        await getLastRegularSlot({
            variables: {
                daycareId: daycareId,
                childId: childId
            }
        })
    }

    useEffect(() => {
        if (user) {
            const daycare: Daycare | undefined = user.daycares.find(
                (daycare: Daycare): boolean => daycare.id === daycareId
            )
            if (daycare) {
                setDaycareInformation(daycare)
                refetchAllRegularSlots(fromDate, toDate)

                setRegularSlotStartDate(undefined)
                setRegularSlotEndDate(undefined)
                getDaycarePlaces({
                    variables: {
                        daycareId: daycare.id
                    }
                })
            }
        }
    }, [childId, daycareId])

    useEffect(() => {
        if (daycareInformation) {
            const hiddenDays: number[] = getClosingDaycareDays(daycareInformation.opening)
            setHiddenDays(hiddenDays.length === NUMBER_OF_DAYS ? [] : hiddenDays)
        }
    }, [daycareInformation])

    function handleSelect(info: DateSelectArg): void {
        if (!moment(info.start).isBefore(moment().startOf('day'))) {
            const startDate: Moment = moment(info.start)
            const endDate: Moment = moment(info.end).subtract(1, 'd')

            setRegularSlotStartDate(startDate.format('DD-MM-YYYY'))
            setRegularSlotEndDate(endDate.format('DD-MM-YYYY'))

            const days: number[] = []

            while (startDate.isSameOrBefore(endDate)) {
                days.push(startDate.day())
                startDate.add(1, 'day')
            }

            const uniqueDays: number[] = days
                .filter((value: number, index: number): boolean => days.indexOf(value) === index)
                .filter((day: number) => !hiddenDays.includes(day))
                .map((day: number) => (day + 6) % 7)

            setRegularSlotDay(uniqueDays)

            setAddRegularSlotDialog(true)
        }
    }

    const [showSlotDialogOpen, setShowSlotDialogOpen] = useState<boolean>(false)

    const [selectedEvent, setSelectedEvent] = useState<EventCalendar | null>(null)
    const [calendarEvents, setCalendarEvents] = useState<EventCalendar[]>([])

    function handleEventClickArg(info: EventClickArg): void {
        setShowSlotDialogOpen(true)
        const event: EventCalendar = info.event.extendedProps as EventCalendar
        setSelectedEvent({
            ...event,
            start: info.event.start ? info.event.start : new Date(),
            end: info.event.end ? info.event.end : new Date()
        })
    }

    const [closingDaysList, setClosingDaysList] = useState<Array<ClosingDay>>([])

    const [getClosingDays, { data: dataClosingDay }] = useLazyQuery(GET_CLOSING_DAY, {
        fetchPolicy: 'network-only',
        variables: {
            daycare_id: daycareInformation?.id,
            from: moment().format('DD-MM-YYYY')
        }
    })

    useEffect(() => {
        if (dataClosingDay && dataClosingDay.getClosingDay && dataClosingDay.getClosingDay.length > 0) {
            setClosingDaysList(dataClosingDay.getClosingDay)
        }
    }, [dataClosingDay])

    useEffect(() => {
        const datesClosingDays: Moment[] = getDateClosingDays(closingDaysList)
        setCalendarEvents([
            ...dataRegularSlots(
                regularSlots,
                datesClosingDays,
                daycareInformation ? daycareInformation.type : DaycareType.daycare,
                user?.role
            ),
            ...dataClosingDays(closingDaysList)
        ])
    }, [regularSlots, closingDaysList])

    const isMobile: boolean = useScreenSize()

    return (
        <>
            <div className={styles.TitleAndSelector}>
                <div className={styles.Title}>
                    <Title text={t('regularSlots')} className={styles.Text} />
                    {showRegularInformation ? (
                        <KeyboardArrowUp onClick={() => setShowRegularInformation(false)} className={styles.Arrow} />
                    ) : (
                        <KeyboardArrowDown onClick={() => setShowRegularInformation(true)} className={styles.Arrow} />
                    )}
                </div>
                {showRegularInformation && user?.currentDaycareRights.canWriteContracts && (
                    <div className={styles.Input}>
                        <ButtonTextIcon
                            type={isMobile ? 'add' : 'custom'}
                            customText={t('createRegular')}
                            customIcon={<Add />}
                            className={styles.Button}
                            onClick={() => setAddRegularSlotDialog(true)}
                            disabled={!user.currentDaycareRights.canWriteContracts}
                        />
                    </div>
                )}
            </div>
            {showRegularInformation && daycareInformation && (
                <>
                    <ChildPlanning
                        fetchData={refetchAllRegularSlots}
                        handleEventClick={handleEventClickArg}
                        handleSelect={handleSelect}
                        currentDaycare={daycareInformation}
                        hiddenDays={hiddenDays}
                        calendarEvents={calendarEvents}
                    />
                    {lastRegularSlot && (
                        <div className={styles.TextContainer}>
                            {t('lastRegularSlot')}
                            {format(parse(lastRegularSlot.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                locale: getLocalFromLanguage()
                            })}
                        </div>
                    )}
                </>
            )}
            {addRegularSlotDialog && user?.currentDaycareRights.canWriteContracts && daycareInformation && (
                <AddRegularSlotsDialog
                    open={addRegularSlotDialog}
                    handleClose={() => setAddRegularSlotDialog(false)}
                    childId={childId}
                    daycare={daycareInformation}
                    startDateString={regularSlotStartDate ? regularSlotStartDate : moment().format('DD-MM-YYYY')}
                    endDateString={regularSlotEndDate ? regularSlotEndDate : moment().format('DD-MM-YYYY')}
                    day={regularSlotDay}
                    refetchRegularSlots={() => refetchAllRegularSlots(fromDate, toDate)}
                    places={places}
                />
            )}
            {user && showSlotDialogOpen && daycareInformation && (
                <ShowSlot
                    open={showSlotDialogOpen}
                    handleClose={() => setShowSlotDialogOpen(false)}
                    selectedEvent={selectedEvent}
                    daycare={daycareInformation}
                    refetchData={() => refetchAllRegularSlots(fromDate, toDate)}
                    childs={[]}
                    calendarEvents={calendarEvents}
                    places={places}
                    isParent={false}
                    staffsListData={[]}
                    works={[]}
                    canEditRegular={user.currentDaycareRights.canWriteContracts}
                    canEditStaffWorks={user.currentDaycareRights.canWriteStaffWorks}
                    lastRegularSlotDate={
                        lastRegularSlot ? lastRegularSlot.date : moment().add(1, 'year').format('DD-MM-YYYY')
                    }
                />
            )}
        </>
    )
}

export default ChildPlanningHandler
